import React, { useState, useEffect } from 'react';
import { systemAdminAPI } from '../../../services/api/systemAdminAPI';
import toast from 'react-hot-toast';
import PerformanceMonitor from '../../../components/PerformanceMonitor';
import { 
  Users, 
  Building2, 
  TrendingUp, 
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Heart,
  Shield,
  Database,
  Zap
} from 'lucide-react';

const SystemAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    totalUsers: 0,
    totalPatients: 0,
    todayRecordings: 0,
    systemHealth: 'Good'
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState({ loadTime: 0, score: 'Good' });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    const endTiming = window.startPerformanceTiming ? window.startPerformanceTiming() : null;
    
    try {
      setLoading(true);
      
      // Make parallel API calls for better performance
      const [statsResponse, activityResponse] = await Promise.all([
        systemAdminAPI.getDashboardStats(),
        systemAdminAPI.getRecentActivity(5)
      ]);
      
      const stats = statsResponse.data;
      const activities = activityResponse.data;
      
      // Set dashboard statistics
      setStats({
        totalTenants: stats.tenants.total,
        activeTenants: stats.tenants.active,
        totalUsers: stats.users.total,
        totalPatients: stats.patients.total,
        todayRecordings: stats.recordings.today,
        systemHealth: stats.system_health
      });

      // Set recent activity from API
      setRecentActivity(activities.map(activity => ({
        id: activity.id,
        type: activity.type,
        message: activity.message,
        time: new Date(activity.time).toLocaleString(),
        status: activity.status
      })));

      // Set system alerts based on real data
      setSystemAlerts([
        { 
          id: 1, 
          type: stats.recordings.completion_rate < 80 ? 'warning' : 'success',
          message: `Recording completion rate: ${stats.recordings.completion_rate.toFixed(1)}%`, 
          priority: 'medium' 
        },
        { 
          id: 2, 
          type: stats.system_health === 'Critical' ? 'error' : 'info',
          message: `System health: ${stats.system_health}`, 
          priority: stats.system_health === 'Critical' ? 'high' : 'low' 
        },
        { 
          id: 3, 
          type: stats.tenants.active > 0 ? 'success' : 'warning',
          message: `${stats.tenants.active} of ${stats.tenants.total} tenants active`, 
          priority: 'low' 
        }
      ]);
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data. Please check your connection and try again.');
      
      // Don't use fallback data - show error state instead
      setStats({
        totalTenants: 0,
        activeTenants: 0,
        totalUsers: 0,
        totalPatients: 0,
        todayRecordings: 0,
        systemHealth: 'Unknown'
      });
      
      setRecentActivity([]);
      setSystemAlerts([
        { 
          id: 1, 
          type: 'error',
          message: 'Unable to load dashboard data. Please refresh the page or contact support.', 
          priority: 'high' 
        }
      ]);
    } finally {
      setLoading(false);
      if (endTiming) {
        endTiming();
      }
    }
  };

  const handlePerformanceChange = (loadTime, score) => {
    setPerformanceData({ loadTime, score });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  // Show error state if data couldn't be loaded
  if (stats.systemHealth === 'Unknown') {
    return (
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0B1C2C] mb-2">System Overview</h1>
          <p className="text-[#3D3D3D]">Complete platform monitoring and management dashboard</p>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">Dashboard Data Unavailable</h3>
              <p className="text-red-700 mt-1">
                Unable to load dashboard statistics. Please check your connection and try refreshing the page.
              </p>
              <button 
                onClick={loadDashboardData}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry Loading Dashboard
              </button>
            </div>
          </div>
        </div>
        
        <PerformanceMonitor onLoadTimeChange={handlePerformanceChange} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0B1C2C] mb-2">System Overview</h1>
        <p className="text-[#3D3D3D]">Complete platform monitoring and management dashboard</p>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
          <div className="flex items-center">
            <div className="p-3 bg-[#DCEAF5] rounded-lg">
              <Building2 className="h-6 w-6 text-[#207CE5]" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[#3D3D3D]">Total Tenants</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">{stats.totalTenants}</p>
              <p className="text-xs text-[#008080]">{stats.activeTenants} active</p>
            </div>
          </div>
        </div>

        <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
          <div className="flex items-center">
            <div className="p-3 bg-[#DCEAF5] rounded-lg">
              <Users className="h-6 w-6 text-[#00BCD4]" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[#3D3D3D]">Total Users</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">{stats.totalUsers}</p>
              <p className="text-xs text-[#207CE5]">Across all tenants</p>
            </div>
          </div>
        </div>

        <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
          <div className="flex items-center">
            <div className="p-3 bg-[#DCEAF5] rounded-lg">
              <Heart className="h-6 w-6 text-[#4682B4]" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[#3D3D3D]">Today's Recordings</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">{stats.todayRecordings}</p>
              <p className="text-xs text-[#008080]">+12% from yesterday</p>
            </div>
          </div>
        </div>

        <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
          <div className="flex items-center">
            <div className="p-3 bg-[#DCEAF5] rounded-lg">
              <Shield className="h-6 w-6 text-[#008080]" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-[#3D3D3D]">System Health</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">{stats.systemHealth}</p>
              <div className="flex items-center">
                <CheckCircle className="h-3 w-3 text-[#008080] mr-1" />
                <p className="text-xs text-[#008080]">All systems operational</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5]">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#0B1C2C]">Recent Activity</h2>
              <Activity className="h-5 w-5 text-[#3D3D3D]" />
            </div>
          </div>
          <div className="p-6">
            {recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      activity.status === 'success' ? 'bg-[#008080]' :
                      activity.status === 'warning' ? 'bg-[#00BCD4]' :
                      'bg-[#207CE5]'
                    }`}></div>
                    <div className="flex-1">
                      <p className="text-sm text-[#0B1C2C]">{activity.message}</p>
                      <p className="text-xs text-[#3D3D3D]">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-[#3D3D3D] mx-auto mb-4" />
                <p className="text-[#3D3D3D]">No recent activity available</p>
              </div>
            )}
          </div>
        </div>

        {/* System Alerts */}
        <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5]">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#0B1C2C]">System Alerts</h2>
              <AlertTriangle className="h-5 w-5 text-[#3D3D3D]" />
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {systemAlerts.map((alert) => (
                <div key={alert.id} className={`p-4 rounded-lg border ${
                  alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                  alert.type === 'success' ? 'bg-green-50 border-green-200' :
                  'bg-[#DCEAF5] border-[#207CE5]'
                }`}>
                  <div className="flex items-start">
                    {alert.type === 'warning' && <AlertTriangle className="h-4 w-4 text-[#00BCD4] mt-0.5 mr-2" />}
                    {alert.type === 'success' && <CheckCircle className="h-4 w-4 text-[#008080] mt-0.5 mr-2" />}
                    {alert.type === 'info' && <Clock className="h-4 w-4 text-[#207CE5] mt-0.5 mr-2" />}
                    <div>
                      <p className="text-sm font-medium text-[#0B1C2C]">{alert.message}</p>
                      <p className="text-xs text-[#3D3D3D] capitalize">Priority: {alert.priority}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5]">
        <div className="p-6 border-b border-[#DCEAF5]">
          <h2 className="text-lg font-semibold text-[#0B1C2C]">Quick Actions</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center space-x-3 p-4 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5] transition-colors">
              <Building2 className="h-6 w-6 text-[#4682B4]" />
              <div className="text-left">
                <p className="font-medium text-[#0B1C2C]">Add New Tenant</p>
                <p className="text-sm text-[#3D3D3D]">Create a new healthcare organization</p>
              </div>
            </button>
            
            <button className="flex items-center space-x-3 p-4 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5] transition-colors">
              <Database className="h-6 w-6 text-[#207CE5]" />
              <div className="text-left">
                <p className="font-medium text-[#0B1C2C]">System Backup</p>
                <p className="text-sm text-[#3D3D3D]">Create full system backup</p>
              </div>
            </button>
            
            <button className="flex items-center space-x-3 p-4 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5] transition-colors">
              <BarChart3 className="h-6 w-6 text-[#00BCD4]" />
              <div className="text-left">
                <p className="font-medium text-[#0B1C2C]">Generate Report</p>
                <p className="text-sm text-[#3D3D3D]">Platform usage analytics</p>
              </div>
            </button>
          </div>
        </div>
      </div>
      
      <PerformanceMonitor onLoadTimeChange={handlePerformanceChange} />
    </div>
  );
};

export default SystemAdminDashboard;